import React, { Children, useCallback, useMemo, useRef } from 'react';
import classNames from 'classnames';
import { Box, type Types } from '@fiverr-private/theme';
import { TabListProps } from '../types';
import { useTabContext } from '../TabContext';
import { renderTabChild } from './helpers';
import { getStylingProps } from './style';
import * as classes from './style.ve.css';
import Indicator from './Indicator';

const TabList = ({ children, renderChildren = renderTabChild, ...props }: TabListProps) => {
  const context = useTabContext();
  const { isFocusedRef, variant, placement, tabsRef } = context;
  const tabListRef = useRef<HTMLDivElement>(null);
  const orientation: Types.Box['aria-orientation'] =
    placement === 'top' || placement === 'bottom' ? 'horizontal' : 'vertical';
  tabsRef.current.length = Children.count(children);

  const focusHandler = useCallback(() => {
    isFocusedRef.current = true;
  }, [isFocusedRef]);

  const blurHandler = useCallback(() => {
    isFocusedRef.current = false;
  }, [isFocusedRef]);

  /* eslint-disable react-hooks/exhaustive-deps */
  const filteredTabs = useMemo(
    () =>
      Array.from(tabListRef.current?.children || []).filter((child) =>
        child.tagName.toLowerCase() === 'button'
          ? child.getAttribute('role') === 'tab'
          : child.children[0]?.role === 'tab'
      ),
    [tabListRef.current?.children?.length]
  );

  return (
    <Box
      role="tablist"
      aria-orientation={orientation}
      className={classNames({
        [classes.left]: placement === 'left' && variant === 'default',
        [classes.right]: placement === 'right' && variant === 'default',
        [classes.top]: placement === 'bottom' && variant === 'default',
        [classes.bottom]: placement === 'top' && variant === 'default',
      })}
      onFocus={focusHandler}
      onBlur={blurHandler}
      ref={tabListRef}
      {...getStylingProps(variant, placement)}
      {...props}
    >
      {Children.map(Children.toArray(children), renderChildren(context))}
      {React.Children.count(children) === filteredTabs.length && (
        <Indicator
          variant={variant}
          placement={placement}
          orientation={orientation}
          isFocusedRef={isFocusedRef}
          tabListRef={tabListRef}
        />
      )}
    </Box>
  );
};

TabList.displayName = 'TabList';

export default TabList;
